/**
 * THE TOP SLOT (B8): the route meter during a select, the bloom summary during
 * a category bloom. One slot, never both at rest (§A.2).
 *
 *  meter   enters at t = 0 (fy 12, blur 5 → 0, 420 settle), counts km and
 *          minutes with the eased p of route:progress (Newsreader, tabular),
 *          then at Tarr − 120 FLIPs into the card's Facts (420 settle) and
 *          fades; the Facts figures take over (§E.1).
 *  bloom   enters with the category label; the count steps up by one on each
 *          bloom:arrive (no tweening between integers); "nearest {min} min"
 *          fades in at the first arrival (§E.4). Reduced motion: final text.
 *
 * Centred between insets.left and W − insets.right (mobile: top 68, centred).
 * aria-hidden (§H.2): announcements carry it. Numbers are written straight to
 * the DOM; React only mounts and unmounts the pills.
 */
import "./card.css";
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import { displayTravel } from "@/contracts/data";
import { EASE_CSS, Meta, cx, useReducedMotion } from "@/design";
import { on } from "@/store/bus";
import { selectCategory, selectPlace } from "@/store/selectors";
import { getApp, useApp } from "@/store/store";
import { LEAVE_MS } from "./cardState";
import { flipTo, flipTransform, onScreen, settledRect, type Rect } from "./flip";
import { bloomAllUpcoming, bloomHasNearest, bloomNoun, fmtKm, minFig, nearestMinutes } from "./format";
import { useSequence } from "./hooks";
import { getDockTarget, getProgress, onSequence } from "./sequence";

/** §E.1: the meter docks over 420 settle; the glass goes at 0.7 of that. */
export const DOCK_MS = 420;

type MeterState = { id: string; seq: number; key: number; state: "count" | "dock" | "leave" };
type BloomState = { cat: string; key: number; leaving: boolean };
/** What the bus has said about the current bloom. `named`: a bloom event for it arrived. */
type BloomLive = { cat: string | null; arrived: number; done: boolean; named: boolean };

const rectOf = (el: Element): Rect => {
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
};

/** Where the dock target will sit once the entering card has settled. */
function dockRect(): Rect | null {
  const t = getDockTarget();
  if (!t) return null;
  const fig = rectOf(t.fig);
  const card = t.card ?? t.fig.closest<HTMLElement>(".ld-loc-card");
  if (!card || card.classList.contains("ld-loc-card--embedded")) return fig;
  const parent = card.offsetParent as HTMLElement | null;
  const pr = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };
  const layoutBox = { left: pr.left + card.offsetLeft + (parent?.clientLeft ?? 0), top: pr.top + card.offsetTop + (parent?.clientTop ?? 0) };
  return settledRect(fig, rectOf(card), layoutBox, card.offsetWidth);
}

export function RouteMeter() {
  const seq = useSequence();
  const layout = useApp((s) => s.layout);
  const kiosk = useApp((s) => s.kiosk) || layout === "kiosk";
  const iLeft = useApp((s) => s.insets.left);
  const iRight = useApp((s) => s.insets.right);
  const bloomCategory = useApp((s) => s.bloomCategory);
  const hasData = useApp((s) => !!s.data);
  const reduced = useReducedMotion();
  const keys = useRef(0);

  /* ── meter lifecycle, from the sequence snapshot ── */
  const [meter, setMeter] = useState<MeterState | null>(null);
  useLayoutEffect(() => {
    const id = seq.id;
    if (seq.phase === "drawing" && id) {
      setMeter((m) => {
        if (m && m.state === "count") return m.id === id && m.seq === seq.seq ? m : { ...m, id, seq: seq.seq };
        return { id, seq: seq.seq, key: ++keys.current, state: "count" };
      });
    } else if (seq.phase === "docked") {
      setMeter((m) => (m && m.state === "count" ? { ...m, state: seq.drew && m.seq === seq.seq ? "dock" : "leave" } : m));
    } else {
      setMeter((m) => (m && m.state === "count" ? { ...m, state: "leave" } : m));
    }
  }, [seq.phase, seq.id, seq.seq, seq.drew]);

  const pillRef = useRef<HTMLDivElement>(null);
  const glassRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLSpanElement>(null);
  const kmFigRef = useRef<HTMLSpanElement>(null);
  const kmRef = useRef<HTMLSpanElement>(null);
  const minRef = useRef<HTMLSpanElement>(null);
  const minUnitRef = useRef<HTMLElement>(null);

  // counting: route:progress → textContent (≤ 30 Hz)
  const meterId = meter?.id ?? null;
  const counting = meter?.state === "count";
  useLayoutEffect(() => {
    if (!meterId || !counting) return undefined;
    const place = selectPlace(getApp(), meterId);
    const t = place ? displayTravel(place) : null;
    if (!t) return undefined;
    const write = (p: number) => {
      const m = minFig(Math.round(t.minutes * p));
      if (kmRef.current) kmRef.current.textContent = fmtKm(t.km * p);
      if (minRef.current) minRef.current.textContent = m.value;
      if (minUnitRef.current) minUnitRef.current.textContent = m.unit;
    };
    const pr = getProgress();
    write(pr && pr.id === meterId ? pr.p : 0);
    return onSequence("progress", (e) => {
      if (e.id === meterId) write(e.p);
    });
  }, [meterId, counting]);

  // dock: FLIP toward the card's km figure, then hide
  const meterKey = meter?.key;
  const meterState = meter?.state;
  useEffect(() => {
    if (meterKey == null || (meterState !== "dock" && meterState !== "leave")) return undefined;
    let raf = 0;
    let post: ReturnType<typeof setTimeout> | undefined;
    const anims: Animation[] = [];
    const done = setTimeout(() => setMeter((m) => (m && m.key === meterKey ? null : m)), meterState === "dock" ? DOCK_MS + 20 : LEAVE_MS);
    if (meterState === "dock") {
      const content = contentRef.current;
      const glass = glassRef.current;
      const fig = kmFigRef.current;
      if (content && glass && fig && typeof content.animate === "function") {
        // §E.1: the pill (glass and figures together) fades over the flight's own 420 settle, so no empty
        // capsule stays behind and the figures are all but gone when the Facts take over (250 ms in, card.css).
        const fade = { duration: DOCK_MS, easing: EASE_CSS.settle, fill: "forwards" } as const;
        anims.push(glass.animate([{ opacity: 1 }, { opacity: 0 }], fade));
        anims.push(content.animate([{ opacity: 1 }, { opacity: 0 }], fade));
        // The flight is measured after the card's first frame, when layout is clean: the dock task only
        // writes (no forced layout as the card mounts, QA L2-07).
        raf = requestAnimationFrame(() => {
          post = setTimeout(() => {
            if (!content.isConnected) return;
            const target = dockRect();
            if (!target || !onScreen(target, window.innerWidth, window.innerHeight)) return;
            const from = rectOf(fig);
            const box = rectOf(content);
            content.style.transformOrigin = `${(from.left - box.left).toFixed(1)}px ${(from.top + from.height / 2 - box.top).toFixed(1)}px`;
            const f = flipTo(from, target);
            const left = Math.max(DOCK_MS - (anims[0]?.currentTime != null ? Number(anims[0].currentTime) : 0), 120);
            anims.push(content.animate([{ transform: "none" }, { transform: flipTransform(f) }], { duration: left, easing: EASE_CSS.settle, fill: "forwards" }));
          }, 0);
        });
      }
    }
    return () => {
      clearTimeout(done);
      cancelAnimationFrame(raf);
      if (post) clearTimeout(post);
      // the pill unmounts after this; nothing to restore
    };
  }, [meterKey, meterState]);

  /* ── bloom summary lifecycle ── */
  const bloomWanted = !!bloomCategory && hasData && seq.id == null && !meter;
  const [bloom, setBloom] = useState<BloomState | null>(null);
  useLayoutEffect(() => {
    if (bloomWanted && bloomCategory) {
      setBloom((b) => (b && b.cat === bloomCategory && !b.leaving ? b : { cat: bloomCategory, key: ++keys.current, leaving: false }));
    } else {
      setBloom((b) => (b && !b.leaving ? { ...b, leaving: true } : b));
    }
  }, [bloomWanted, bloomCategory]);
  const bloomLeaving = bloom?.leaving;
  const bloomKey = bloom?.key;
  useEffect(() => {
    if (!bloomLeaving || bloomKey == null) return undefined;
    const t = setTimeout(() => setBloom((b) => (b && b.key === bloomKey && b.leaving ? null : b)), LEAVE_MS);
    return () => clearTimeout(t);
  }, [bloomLeaving, bloomKey]);

  // what the bus has said about the current bloom (outlives the pill, so a pill
  // that returns after a deselect shows where the bloom got to)
  const live = useRef<BloomLive>({ cat: null, arrived: 0, done: false, named: false });
  const liveSubs = useRef(new Set<() => void>());
  useEffect(() => {
    const L = live.current;
    const ping = () => liveSubs.current.forEach((f) => f());
    const offs = [
      on("bloom:start", (e) => {
        L.cat = e.categoryId;
        L.arrived = 0;
        L.done = false;
        L.named = true;
        ping();
      }),
      on("bloom:arrive", (e) => {
        if (L.cat !== e.categoryId || !L.named) {
          L.cat = e.categoryId;
          L.arrived = 0;
          L.done = false;
          L.named = true;
        }
        L.arrived += 1;
        ping();
      }),
      on("bloom:done", (e) => {
        if (L.cat === e.categoryId) {
          L.done = true;
          ping();
        }
      }),
    ];
    return () => offs.forEach((o) => o());
  }, []);
  // a new category that the bus hasn't named yet starts from zero
  useEffect(() => {
    const L = live.current;
    if (bloomCategory && L.cat !== bloomCategory) {
      L.cat = bloomCategory;
      L.arrived = 0;
      L.done = false;
      L.named = false;
    }
  }, [bloomCategory]);

  const slotCls = cx("ld-loc-card-slot", layout === "mobile" && "ld-loc-card-slot--mobile", kiosk && "ld-loc-card-slot--kiosk");
  const slotStyle: CSSProperties = layout === "mobile" ? { left: 12, right: 12 } : { left: iLeft, right: iRight };

  return (
    <div className={slotCls} style={slotStyle} aria-hidden="true">
      {bloom && (
        <BloomPill key={bloom.key} cat={bloom.cat} leaving={bloom.leaving} reduced={reduced} live={live.current} liveSubs={liveSubs.current} />
      )}
      {meter && (
        <MeterPill
          key={meter.key}
          id={meter.id}
          state={meter.state}
          refs={{ pillRef, glassRef, contentRef, kmFigRef, kmRef, minRef, minUnitRef }}
        />
      )}
    </div>
  );
}

/* ── the meter pill ───────────────────────────────────────────────── */

interface MeterRefs {
  pillRef: RefObject<HTMLDivElement | null>;
  glassRef: RefObject<HTMLSpanElement | null>;
  contentRef: RefObject<HTMLSpanElement | null>;
  kmFigRef: RefObject<HTMLSpanElement | null>;
  kmRef: RefObject<HTMLSpanElement | null>;
  minRef: RefObject<HTMLSpanElement | null>;
  minUnitRef: RefObject<HTMLElement | null>;
}

function MeterPill({ id, state, refs }: { id: string; state: MeterState["state"]; refs: MeterRefs }) {
  const place = useApp((s) => selectPlace(s, id));
  const t = place ? displayTravel(place) : null;
  const finalKm = t ? fmtKm(t.km) : "0.0";
  const finalMin = t ? minFig(t.minutes) : { value: "0", unit: "min" as const };
  return (
    <div ref={refs.pillRef} className={cx("ld-loc-card-pill", state === "leave" ? "is-leaving" : "ld-in")}>
      <span ref={refs.glassRef} className="ld-loc-card-pill__glass ld-glass" />
      <span ref={refs.contentRef} className="ld-loc-card-pill__content">
        <span ref={refs.kmFigRef} className="ld-fig ld-num ld-loc-card-pill__fig">
          <span className="ld-loc-card-pill__num">
            <span className="ld-loc-card-pill__ghost">{finalKm}</span>
            <span ref={refs.kmRef}>0.0</span>
          </span>
          <small>km</small>
        </span>
        <span className="ld-loc-card-pill__sep">·</span>
        <span className="ld-fig ld-num ld-loc-card-pill__fig">
          <span className="ld-loc-card-pill__num">
            <span className="ld-loc-card-pill__ghost">{finalMin.value}</span>
            <span ref={refs.minRef}>0</span>
          </span>
          <small ref={refs.minUnitRef}>min</small>
        </span>
        <Meta className="ld-loc-card-pill__meta">Off-peak</Meta>
      </span>
    </div>
  );
}

/* ── the bloom summary pill ───────────────────────────────────────── */

/** After this long without a bloom event the summary shows its final text. */
export const BLOOM_QUIET_MS = 2600;

function BloomPill({
  cat,
  leaving,
  reduced,
  live,
  liveSubs,
}: {
  cat: string;
  leaving: boolean;
  reduced: boolean;
  live: BloomLive;
  liveSubs: Set<() => void>;
}) {
  const label = useApp((s) => selectCategory(s, cat)?.label ?? "");
  const n = useApp((s) => (s.data ? s.data.places.filter((p) => p.category === cat && !p.isSite).length : 0));
  const nearest = useApp((s) => (s.data ? nearestMinutes(s.data.places, cat) : null));
  const upcoming = useApp((s) => (s.data ? bloomAllUpcoming(s.data.places, cat) : false));
  const hasNearest = bloomHasNearest(cat) && nearest != null;
  const countRef = useRef<HTMLSpanElement>(null);
  const nounRef = useRef<HTMLSpanElement>(null);
  const headRef = useRef<HTMLSpanElement>(null);
  const nearRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    let k = -1;
    const show = (v: number) => {
      const c = Math.max(0, Math.min(n, v));
      if (c === k) return;
      k = c;
      if (countRef.current) countRef.current.textContent = String(c);
      if (nounRef.current) nounRef.current.textContent = bloomNoun(cat, c || n, "places", upcoming);
      headRef.current?.classList.toggle("is-off", c === 0);
      nearRef.current?.classList.toggle("is-off", c === 0);
    };
    let quiet: ReturnType<typeof setTimeout> | undefined;
    const sync = () => {
      const mine = live.cat === cat && live.named;
      if (mine && quiet) {
        clearTimeout(quiet);
        quiet = undefined;
      }
      show(mine ? (live.done ? n : live.arrived) : 0);
    };
    if (reduced) {
      show(n);
      return undefined;
    }
    sync();
    liveSubs.add(sync);
    // a bloom the bus never names (or one that finished before we mounted) still ends in its final text
    if (!(live.cat === cat && live.named)) quiet = setTimeout(() => show(n), BLOOM_QUIET_MS);
    return () => {
      liveSubs.delete(sync);
      if (quiet) clearTimeout(quiet);
    };
  }, [cat, n, upcoming, reduced, live, liveSubs]);

  const finalNoun = bloomNoun(cat, n, "places", upcoming);
  return (
    <div className={cx("ld-loc-card-pill ld-loc-card-pill--bloom", leaving ? "is-leaving" : "ld-in")}>
      <span className="ld-loc-card-pill__glass ld-glass" />
      <span className="ld-loc-card-pill__content">
        <Meta className="ld-loc-card-pill__meta">{label}</Meta>
        <span className="ld-loc-card-pill__stack">
          <span className="ld-loc-card-pill__ghost" aria-hidden="true">
            <span className="ld-fig ld-num ld-loc-card-pill__fig">{n}</span>
            <span className="ld-loc-card-pill__words">{finalNoun}</span>
            {hasNearest && (
              <span className="ld-loc-card-pill__nearest">
                <span className="ld-loc-card-pill__quiet">· nearest</span>
                <NearestFig min={nearest} />
                <span className="ld-loc-card-pill__quiet">off-peak</span>
              </span>
            )}
          </span>
          <span className="ld-loc-card-pill__row">
            <span ref={headRef} className="ld-loc-card-pill__count is-off">
              <span ref={countRef} className="ld-fig ld-num ld-loc-card-pill__fig">
                0
              </span>
              <span ref={nounRef} className="ld-loc-card-pill__words">
                {finalNoun}
              </span>
            </span>
            {hasNearest && (
              <span ref={nearRef} className="ld-loc-card-pill__nearest is-off">
                <span className="ld-loc-card-pill__quiet">· nearest</span>
                <NearestFig min={nearest} />
                <span className="ld-loc-card-pill__quiet">off-peak</span>
              </span>
            )}
          </span>
        </span>
      </span>
    </div>
  );
}

function NearestFig({ min }: { min: number | null }) {
  const m = minFig(min ?? 0);
  return (
    <span className="ld-fig ld-num ld-loc-card-pill__fig ld-loc-card-pill__fig--sm">
      {m.value}
      <small>{m.unit}</small>
    </span>
  );
}
